import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DOCUMENT } from '@angular/common';

import { Product } from '../../../../models/product.model';
import { OrderItemDelivery } from '../../../../models/order-item-delivery.model';
import { Order } from '../../../../models/order.model';
import { OrderItem } from '../../../../models/order-item.model';
import { AuthService } from '../../../../services/auth.service';
import { ECOMMERCE_TYPE } from '../../../../constants/order-types';

import { ModalService } from '../../../../services/modal.service';
import { isEmptyArray } from '../../../../shared/utils';
import {
  TrackingNumber,
  UNKNOWN_SLUG,
} from '../../../../interfaces/tracking-number';
import * as ORDER_STATES from '../../../../constants/order-states';
import * as TRACKING_STATES from '../../../../constants/tracking-states';
import { computeShipmentLabel } from '../../../../shared/helpers';
import { PaymentService } from '../../../../services/payments.service';
import { MobileCheckServiceService } from '../../../../services/mobile-check-service.service';
import { MultiTenantService } from '../../../../services/multi-tenant.service';
import { ShippingMethod } from '../../../../models/shipping-method';
import {AddressService} from '../../../../services/address.service';

@Component({
  selector: 'app-product-order-list-item-delivery',
  templateUrl: './product-order-list-item-delivery-component.html',
  styleUrls: ['./product-order-list-item-delivery-component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProductOrderListItemDeliveryComponent implements OnInit {
  @Input() product: Product;
  @Input() delivery: OrderItemDelivery;
  @Input() order: Order;
  @Input() orderItem: OrderItem;
  @Input() isLastShipment = false;
  @Input() viewType: string;

  @Output() onRemove = new EventEmitter<OrderItem>();
  @Output() changed = new EventEmitter<OrderItem>();

  @ViewChild('divContainer') divContainer: ElementRef;

  _refreshingTrackingStatus = false;

  shippingMethods: any[] = [];
  isSingleShippingMethod = false;
  trackingNumbers: TrackingNumber[] = [];
  status: string;
  trackingData: string;
  show = 'text';

  private hasShippingMethodError = false;

  @Input() set refreshingTrackingStatus(val: boolean) {
    if (val === false && this._refreshingTrackingStatus) {
      // redraw tracking data when refreshing is finished;
      this.initTrackingData();
    }
    this._refreshingTrackingStatus = val;
  }


  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private ngbModalService: NgbModal,
    private paymentService: PaymentService,
    private mobileCheck: MobileCheckServiceService,
    private multiTenantService: MultiTenantService,
    @Inject(DOCUMENT) private _document: Document,
    private addressService: AddressService
  ) {}

  ngOnInit(): void {
    this.calcAvailableShipmentMethods();
    this.initTrackingData();
  }

  private initTrackingData() {
    this.trackingNumbers = this.delivery.normalizedTrackingNumbers;

    if (this.trackingNumbers.length === 0) {
      // tracking data not available
      this.trackingData = 'N/A';
      this.status =
        this.order.state === ORDER_STATES.COMPLETE_STATE
          ? TRACKING_STATES.DELIVERED
          : TRACKING_STATES.PENDING;
    } else if (this.trackingNumbers.length === 1) {
      const trackingNumber = this.trackingNumbers[0];

      // single tracking number case
      this.status =
        trackingNumber.slug !== UNKNOWN_SLUG
          ? trackingNumber.status
          : this.order.state === ORDER_STATES.COMPLETE_STATE
          ? TRACKING_STATES.DELIVERED
          : TRACKING_STATES.PENDING;
      this.show = 'link';
      if (this.validateTrackingNumber(this.trackingNumbers[0].number)) {
        this.show = 'vendor';
        this.trackingData = this.trackingNumbers[0].number;
      }
    } else {
      // multiple tracking case

      // find global tracking status
      this.status =
        this.trackingNumbers.filter(
          (t) =>
            t.status === TRACKING_STATES.DELIVERED && t.slug !== UNKNOWN_SLUG
        ).length === this.trackingNumbers.length
          ? TRACKING_STATES.DELIVERED
          : TRACKING_STATES.IN_TRANSIT;
      this.show = 'modal';

      this.trackingNumbers.forEach((tracking) => {
        tracking.mode = this.validateTrackingNumber(tracking.number)
          ? 'vendor'
          : 'link';
      });
    }
  }

  validateTrackingNumber(value: string): boolean {
    return value === 'MISC' || value === 'Call Client Service';
  }

  showDetails(content) {
    this.ngbModalService.open(content).result.then(
      () => {},
      () => {}
    );
  }

  isDelivered(val): boolean {
    return val === TRACKING_STATES.DELIVERED;
  }

  /**
   * checks whether item can be purchased
   * in case of BUYING_WINDOW project  ow_purchasable == false - purchase is not available
   */
  get canPurchase(): boolean {
    if (!this.order) {
      return false;
    }

    if (this.paymentService.hasFailedTransactions) {
      return false;
    }

    return (
      this.orderItem.deliveries.length !== 0 &&
      (this.order.type === ECOMMERCE_TYPE
        ? this.orderItem.init_quantity + this.delivery.quantity > 0 ||
          this.product.inStock
        : this.product.ow_purchasable === true)
    );
  }

  get internalOrderLabel(): string {
    return this.order.isWBSDefined ? 'WBS' : 'Internal Order';
  }

  onQuantityChanged(event) {
    const newValue: number = +event.target.value || 0;
    if (this.orderItem.isAvailable) {
      // ensure quantity is not null
      this.delivery.quantity = this.orderItem.validateDeliveryQuantity(
        this.delivery,
        newValue
      );
    }
  }

  onQuantityBlur() {
    // emit change event only after blur event  to keep focus on input
    this.deliveryChanged();
    return true;
  }

  private deliveryChanged() {
    this.orderItem.changed = true;
    this.changed.emit(this.orderItem);
  }

  onHandleRemove() {
    this.onRemove.emit(this.orderItem);
  }

  get canCustomize(): boolean {
    return (
      this.order.isDecisionPoint !== true &&
      this.authService.canEditOrder(this.order) &&
      !this.paymentService.hasFailedTransactions
    );
  }

  public getShipToLabel(delivery: OrderItemDelivery): string {
    return computeShipmentLabel(delivery);
  }

  public getSapInfo(delivery: OrderItemDelivery): string {
    return delivery.cost_center
      ? delivery.cost_center.getLongAccount()
      : this.order.costCenter.getLongAccount();
  }

  private calcAvailableShipmentMethods() {
    const findAvailableShippingMethodsByCountryCode = (
      shippingMethods: ShippingMethod[],
      countryCode: string
    ): ShippingMethod[] | undefined => {
      return shippingMethods.filter((s) => s.country === countryCode);
    };

    if (!this.delivery || !this.product) {
      return;
    }

    this.hasShippingMethodError = false;
    this.isSingleShippingMethod = false;

    const availableShippingMethods = findAvailableShippingMethodsByCountryCode(
      this.product.shipping_methods,
      this.delivery.country_code
    );

    if (isEmptyArray(availableShippingMethods)) {
      this.hasShippingMethodError = true;
      this.delivery.method = null;
      return;
    }

    if (availableShippingMethods.length === 1) {
      this.isSingleShippingMethod = true;
    }

    if (!this.delivery.method) {
      // only  if method is not set yet
      this.delivery.method = availableShippingMethods[0].method;
    }

    this.shippingMethods = availableShippingMethods;
  }

  showDPFaqDialog() {
    this.modalService.showDecisionPointFaq(this.order.attr_decision_point_name);
  }

  get mobile(): boolean {
    return this.mobileCheck.mobile;
  }

  get mobileDevice(): boolean {
    return this.mobileCheck.mobileDevice;
  }

  get tideContainer(): boolean {
    const modal = this._document.querySelector('.order-item-modal');
    if (!!modal) {
      return true;
    }

    if (this.divContainer) {
      return this.divContainer.nativeElement.clientWidth < 665;
    }
    return false;
    // const lg = window.matchMedia('(max-width: 1199px)');
    // return lg.matches;
  }

  get qtyContainerCSS(): string {
    const styles: string[] = [];

    if (this.viewType === 'list-item') {
      styles.push('list-item');
    }

    if (this.canShowInternalOrder) {
      styles.push('col-2');
    } else {
      styles.push('col-3');
    }

    if (this.mobile) {
      styles.push(' px-0 pr-2');
    }

    return styles.join(' ');
  }

  get canShowInternalOrder(): boolean {
    return this.multiTenantService.canShowWBS;
  }
}
